import { Button, ButtonStyles } from "components/common/button";
import { H1 } from "components/common/heading";
import { IconBadge } from "components/common/icon-badge";
import { DefaultLayout } from "layouts/default";
import { usePageTitle } from "lib/common/use-page-title";
import { Flex, Stack, Text } from "@chakra-ui/react";
import { IErrorLayoutProps } from "./interfaces";
import { useMemo } from "react";
import { DEFAULT_SPACING, HALF_SPACING } from "constants/spacing";
import { BUTTON_GRADIENT, BUTTON_GRADIENT_HOVER } from "styles/theme";

const { sizes, variants } = ButtonStyles;

/**
 * Generic error layout that displays an icon, optional status code, heading, message(s), and a stack of actions.
 */
export function ErrorLayout(props: IErrorLayoutProps) {
  const {
    title,
    heading,
    message,
    messageTestId,
    messageTextAlign = "center",
    footnote,
    actions,
    icon
  } = props;

  usePageTitle(title);

  // support either a single message or an array of messages (plain strings or { text, bold }), with each as a paragraph
  const messages = useMemo(() => {
    const list = typeof message === "string" ? [message] : message;
    return list.map((item) => typeof item === "string" ? { text: item } : item);
  }, [message]);

  return (
    <DefaultLayout>
      {icon && <IconBadge>{icon}</IconBadge>}
      {/* statusCode is intentionally not rendered - not relevant to users, see interfaces.ts */}
      <H1 data-testid="heading" fontSize="3xl" textAlign="center" color="white">{heading}</H1>
      <Stack spacing={HALF_SPACING}>
        {messages.map((message, index) => (
          <Text
            key={`message-${index}`}
            data-testid={messageTestId}
            textStyle="body"
            textAlign={messageTextAlign}
            fontWeight={message.bold ? "bold" : undefined}
            color="whiteAlpha.800"
          >
            {message.text}
          </Text>
        ))}
        {footnote}
      </Stack>
      <Flex justifyContent="center" marginTop={DEFAULT_SPACING}>
        <Stack spacing={2} w="full" maxW="240px">
          {actions.map((action) => (
            action.primary ? (
              <Button
                key={action.testId}
                data-testid={action.testId}
                onClick={action.onClick}
                size={sizes.medium}
                variant={variants.primary}
                leftIcon={action.icon}
                color="white"
                border="1px solid"
                borderColor="whiteAlpha.300"
                background={BUTTON_GRADIENT}
                _hover={{ background: BUTTON_GRADIENT_HOVER }}
                _active={{ background: BUTTON_GRADIENT_HOVER }}
              >
                {action.text}
              </Button>
            ) : (
              <Button
                key={action.testId}
                data-testid={action.testId}
                onClick={action.onClick}
                size={sizes.medium}
                variant={variants.ghost}
                leftIcon={action.icon}
                color="whiteAlpha.800"
                _hover={{ bg: "whiteAlpha.200", textDecoration: "none" }}
              >
                {action.text}
              </Button>
            )
          ))}
        </Stack>
      </Flex>
    </DefaultLayout>
  );
}
